import React, { useContext, useMemo, useState } from "react";
import { CorporateContext } from "./CorporateContext";
import { ICorporateContextProps } from "./CorporateContextInterfaces";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { UserContext } from "contexts/UserContext/UserContext";
import { UsersEntity } from "entities/Users/UsersEntity";
import { isApiAdministrator } from "utilities/utilities";

export const CorporateContextProvider: React.FC<ICorporateContextProps> = ({ children }) => {
    const [isFullscreenActive, setIsFullscreenActive] = useState(false);
    // This provider wraps every route in App.tsx, so it also mounts for the API Administrator
    // portal (OS-2557). Neither of these documents is readable by the api_administrator role — the
    // org tree needs corporate/division/region/mill and the profile document selects `mill` — so
    // both are skipped for it. The portal supplies its own CorporateContext value downstream
    // (ApiAdminContextProvider), which is where its profile actually comes from.
    const isApiAdmin = isApiAdministrator();
    const {
        corporations,
        millsWithNoDivision,
        loading: isLoadingCorporationsAndDivisions,
        refetch: refreshCorporationsAndDivisions,
    } = CorporationsEntity.GetMenuCorporateDivisionsRegionsMills(isApiAdmin);
    const divisions = useMemo(() => corporations[0]?.divisions || [], [corporations]);
    const rcaEnabled = useMemo(() => corporations[0]?.rca_enabled, [corporations]);
    const { user_id } = useContext(UserContext);
    const {
        currentUser,
        enabledNotifications,
        enabledNotificationsIds,
        loading: isLoadingCurrentUser,
        refetch: refreshCurrentUser,
    } = UsersEntity.GetCurrentUser(user_id || 0, false, isApiAdmin);

    const handleSetIsFullscreenActive = (value: boolean) => {
        setIsFullscreenActive(value);
    };

    const providerValue = {
        enabledNotifications,
        enabledNotificationsIds,
        rcaEnabled,
        isFullscreenActive,
        handleSetIsFullscreenActive,
        divisions,
        corporations,
        millsWithNoDivision,
        currentUser,
        isLoadingCurrentUser,
        isLoadingCorporationsAndDivisions,
        refreshCorporationsAndDivisions,
        refreshCurrentUser,
    };

    return <CorporateContext.Provider value={providerValue}>{children}</CorporateContext.Provider>;
};
